import moment from 'moment'
import { counterService } from '../../../services/counterService'
import {
  COMPUTED_OPERATION_TYPES,
  COMPUTED_RESERVED_KEYS,
  FIELD_TYPES,
  FORMATS,
} from '../../../utility/constants'
import { getCurrentFinancialYear } from '../../../utility/utilityFunctions'
import { getDefaultValueForField } from './fieldUtil'
import { getFieldId, getSelectableTableId } from './utils'
import { processDataService } from '../../../services/processDataService'
import { indexedDbStorage } from '../../../utility/indexedDbStorage'

const DATE_COMPUTATION_FIELDS = {
  FIELD: 'field',
  ACTIVITY: 'activity',
  LIST: 'list',
}

const DATE_COMPUTATION_DAYS_CATEGORY = {
  CALENDER_DAYS: 'calender-days',
  WORKING_DAYS: 'working-days',
}

const ACTIVITY_VERSION = {
  INITIAL_VERSION: 'initial-version',
  LATEST_VERSION: 'latest-version',
}

export const DROPDOWN_DEFAULT_VALUE = -1

export const autoComputeFieldValue = async (
  allFields = [],
  allValues,
  listDataMetadata,
  otherData,
  computeFields = [],
) => {
  // declare and initialize empty object of computed fields allComputedFieldValues

  let allComputedFieldValues = {}

  // find all computed field
  let allComputedFields
  if (computeFields.length <= 0 && !!otherData.tableRow) {
    allComputedFields = computeFields.filter((it) => {
      if (it.is_subfield) {
        return !!otherData.tableRow && it.is_computed
      }
      return it.is_computed
    })
  } else {
    allComputedFields = allFields.filter((it) => {
      if (it.is_subfield) {
        return !!otherData.tableRow && it.is_computed
      }
      return it.is_computed
    })
  }

  // loop through each computed fields
  for (const field of allComputedFields.filter(
    (it) => it.metadata.operation !== COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS,
  )) {
    // get metadata

    const fieldMetadata = field.metadata

    const altComputationFieldTemplateId = fieldMetadata?.alt_field_template_id

    const altComputationListDataIds = fieldMetadata?.alt?.reduce((acc, curr) => {
      return acc.concat(curr.list_data_ids)
    }, [])
    let fieldMetaDataParams = fieldMetadata?.params
    let fieldMetaDataOperation = fieldMetadata?.operation

    if (altComputationFieldTemplateId != null && altComputationListDataIds != null) {
      const altComputationFieldValue = allValues[getFieldId(altComputationFieldTemplateId)]
      if (
        altComputationFieldValue &&
        altComputationListDataIds.includes(altComputationFieldValue)
      ) {
        fieldMetadata.alt.forEach((altObj) => {
          if (altObj.list_data_ids.includes(altComputationFieldValue)) {
            fieldMetaDataParams = altObj?.params || []
            fieldMetaDataOperation = altObj?.operation_alt
          }
        })
      }
    }

    let computedValue = getDefaultComputeFieldValue(fieldMetaDataOperation)

    if (fieldMetaDataOperation !== COMPUTED_OPERATION_TYPES.DATE_COMPUTATION) {
      for (const param of fieldMetaDataParams) {
        let computeValue = getDefaultValueForField(field)

        if (
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN
        ) {
          computeValue = await getParamComputeValueTableColumn(
            param,
            fieldMetaDataOperation,
            fieldMetadata,
            allFields,
            allValues,
            listDataMetadata,
            otherData,
          )
        } else if (
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY ||
          fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT
        ) {
          computeValue = await getParamComputeValue(
            param,
            fieldMetaDataOperation,
            field,
            allFields,
            allValues,
            listDataMetadata,
            otherData,
          )
        }

        if (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
          computedValue = computeValue
        } else {
          if (
            fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY ||
            fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN
          ) {
            computedValue = computedValue * computeValue
          } else {
            if (
              (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT ||
                fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN) &&
              (computeValue == undefined || computeValue == 'undefined')
            ) {
              computedValue = computedValue + ''
            } else {
              computedValue = computedValue + computeValue
            }
          }
        }
      }
    }

    // DATE_COMPUTATION handled separately — no params needed
    if (fieldMetaDataOperation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION) {
      computedValue = await computeDateField(fieldMetadata, allValues, listDataMetadata, otherData)
    }

    if (fieldMetaDataOperation == COMPUTED_OPERATION_TYPES.SUM_ACROSS_PROCESS) {
      if (otherData?.allProcessData) {
        allComputedFieldValues = {
          ...allComputedFieldValues,
          [getFieldId(field.id)]: computedValue,
        }
      }
    } else {
      allComputedFieldValues = {
        ...allComputedFieldValues,
        [getFieldId(field.id)]: computedValue,
      }
    }
  }

  return allComputedFieldValues
}

// Add this export so callers can check if a changed field affects any DATE_COMPUTATION field
export const getDateComputationDependencyFieldIds = (allFields = []) => {
  const deps = new Set()
  allFields
    .filter(
      (f) => f.is_computed && f.metadata?.operation === COMPUTED_OPERATION_TYPES.DATE_COMPUTATION,
    )
    .forEach((f) => {
      const dc = f.metadata?.date_config
      if (dc?.source_date?.field_template_id) {
        deps.add(String(dc.source_date.field_template_id))
      }
      if (dc?.duration?.list_field_template_id) {
        deps.add(String(dc.duration.list_field_template_id))
      }
      if (dc?.duration?.numeric_field_template_id) {
        deps.add(String(dc.duration.numeric_field_template_id))
      }
    })
  return deps
}

const getParamComputeValueTableColumn = async (
  param,
  operation,
  fieldMetadata,
  allFields,
  allValues,
  listDataMetadata,
  otherData,
) => {
  let computeValue = [COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN].includes(operation) ? '' : 0
  if (param?.startsWith('^^')) {
    let fieldId
    let plusOrMinusOne = 1
    const keyProp = param.substring(2).split('.')[1]

    if (
      operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
      operation === COMPUTED_OPERATION_TYPES.SUM
    ) {
      fieldId = param.substring(2).split('.')[0]
      plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
    } else {
      fieldId = param.substring(2).split('.')[0]
    }

    const tableFieldTemplateId = otherData?.tableSubfieldsMap?.find(
      (it) => it.subfield_template_id == fieldId,
    )?.table_field_template_id

    const tableField = allFields.find((it) => it.id == tableFieldTemplateId)
    const field = allFields.find((it) => it.id == fieldId)

    if (tableField?.is_selectable_table) {
      for (const [key, value] of Object.entries(allValues)) {
        if (
          key.includes(`f_${fieldId}__`) &&
          otherData?.selectedTableData[getSelectableTableId(tableFieldTemplateId)]?.includes(
            key.split('__')[1],
          )
        ) {
          let rowValue = value
          if (
            field?.data_structure === FIELD_TYPES.LIST ||
            field?.data_structure === FIELD_TYPES.LIST_MULTI
          ) {
            const fakeAllValue = { [`f_${fieldId}`]: value }
            rowValue = await getFieldValue(
              keyProp,
              fieldId,
              field,
              fakeAllValue,
              listDataMetadata,
              otherData,
            )
          }

          // incorporate mathematical operator as per config and return
          if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
            computeValue += plusOrMinusOne * (Number(rowValue) || 0)
          } else {
            computeValue += rowValue !== undefined && rowValue !== null ? rowValue : ''
          }
        }
      }
    } else {
      for (const [key, value] of Object.entries(allValues)) {
        if (key.includes(`f_${fieldId}__`)) {
          let rowValue = value
          if (
            field?.data_structure === FIELD_TYPES.LIST ||
            field?.data_structure === FIELD_TYPES.LIST_MULTI
          ) {
            const fakeAllValue = { [`f_${fieldId}`]: value }
            rowValue = await getFieldValue(
              keyProp,
              fieldId,
              field,
              fakeAllValue,
              listDataMetadata,
              otherData,
            )
          }

          // incorporate mathematical operator as per config and return
          if (operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN) {
            computeValue += plusOrMinusOne * (Number(rowValue) || 0)
          } else {
            computeValue += rowValue !== undefined && rowValue !== null ? rowValue : ''
          }
        }
      }
    }
  } else {
    console.log('Error: missing implementation of param type: ' + param)
  }
  return computeValue
}

const getParamComputeValue = async (
  param,
  operation,
  field,
  allFields,
  allValues,
  listDataMetadata,
  otherData,
) => {
  try {
    if (param?.startsWith('^^')) {
      /* this section reference values from other fields */
      let fieldId
      let plusOrMinusOne = 1
      const key = param.substring(2).split('.')[1]

      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM
      ) {
        fieldId = param.substring(3).split('.')[0]
        plusOrMinusOne = param.substring(2).split('.')[0].charAt(0) === '-' ? -1 : 1
      } else {
        fieldId = param.substring(2).split('.')[0]
      }

      const field = allFields.find((it) => it.id == fieldId)

      let fieldValue = await getFieldValue(
        key,
        fieldId,
        field,
        allValues,
        listDataMetadata,
        otherData,
      )

      // incorporate mathematical operator as per config and return
      if (
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY ||
        operation === COMPUTED_OPERATION_TYPES.SUM ||
        operation === COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN ||
        operation === COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN
      ) {
        return plusOrMinusOne * fieldValue
      } else {
        return fieldValue // simply return for concat
      }
    } else if (param?.startsWith('**')) {
      /* this section have some fixed value (processReferenceNum, currentFinancialYear) and some generated value 
      which is not directly referencing to any other fields based on some custom business logic (autoIncrementCounter) */
      const paramValue = await getParamValue(param, allValues, field, otherData)
      return paramValue
    } else {
      /* this section param itself is value - so simply return param */
      return param
    }
  } catch (error) {
    return param
  }
}

const getFieldValue = async (key, fieldId, field, allValue, listDataMetadata, otherData) => {
  // find actual value of field
  if (!allValue) {
    return ''
  }
  if (field?.data_structure === FIELD_TYPES.LIST) {
    const listItemId = allValue[getFieldId(fieldId)]
    if (listItemId == DROPDOWN_DEFAULT_VALUE) {
      return ''
    }
    const isDynamic = field?.list_id == null && field?.dynamic_field_type
    if (isDynamic) {
      const dynType = field.dynamic_field_type?.type
      const sourceList =
        dynType === 'users'
          ? otherData?.dynamicListData?.usersList
          : otherData?.dynamicListData?.teamsList
      const item = sourceList?.find((it) => it.id == listItemId)
      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        return item?.value ?? ''
      } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
        return listItemId
      } else {
        return item?.metadata?.[key] ?? ''
      }
    } else {
      let listDataItem = listDataMetadata?.find((it) => it.list_data_id == listItemId)

      if (!listDataItem && field?.list_id) {
        const cacheKey = `list_data_${field.list_id}`
        const cachedListData = await indexedDbStorage.getItem(cacheKey)
        if (cachedListData) {
          const foundItem = cachedListData.find((it) => it.id == listItemId)
          if (foundItem) {
            listDataItem = {
              list_data_id: foundItem.id,
              list_data_value: foundItem.value,
              list_data_metadata: foundItem.metadata,
            }
          }
        }
      }

      /* if it is value get value, else get properties from  metadata*/
      if (key === COMPUTED_RESERVED_KEYS.VALUE) {
        return listDataItem?.list_data_value
      } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
        return listDataItem?.list_data_id || listItemId
      } else {
        return listDataItem?.list_data_metadata?.[key]
      }
    }
  } else if (field?.data_structure === FIELD_TYPES.LIST_MULTI) {
    // LIST_MULTI may be stored as an array (in-session) or a comma-separated string (from DB)
    const rawIds = allValue[getFieldId(fieldId)]
    const selectedIds = Array.isArray(rawIds)
      ? rawIds
      : rawIds
        ? String(rawIds)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : []
    if (selectedIds.length === 0) {
      return ''
    }
    const isDynamic = field?.list_id == null && field?.dynamic_field_type
    const dynType = field?.dynamic_field_type?.type
    const sourceList = isDynamic
      ? dynType === 'users'
        ? otherData?.dynamicListData?.usersList
        : otherData?.dynamicListData?.teamsList
      : null

    const results = await Promise.all(
      selectedIds.map(async (id) => {
        if (isDynamic) {
          const item = sourceList?.find((it) => it.id == id)
          if (key === COMPUTED_RESERVED_KEYS.VALUE) {
            return item?.value ?? ''
          } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
            return id
          } else {
            return item?.metadata?.[key] ?? ''
          }
        } else {
          let listDataItem = listDataMetadata?.find((it) => it.list_data_id == id)

          if (!listDataItem && field?.list_id) {
            const cacheKey = `list_data_${field.list_id}`
            const cachedListData = await indexedDbStorage.getItem(cacheKey)
            if (cachedListData) {
              const foundItem = cachedListData.find((it) => it.id == id)
              if (foundItem) {
                listDataItem = {
                  list_data_id: foundItem.id,
                  list_data_value: foundItem.value,
                  list_data_metadata: foundItem.metadata,
                }
              }
            }
          }

          if (key === COMPUTED_RESERVED_KEYS.VALUE) {
            return listDataItem?.list_data_value ?? ''
          } else if (key === COMPUTED_RESERVED_KEYS.LIST_DATA_ID) {
            return listDataItem?.list_data_id || id
          } else {
            return listDataItem?.list_data_metadata?.[key] ?? ''
          }
        }
      }),
    )
    return results.filter(Boolean).join(', ')
  } else {
    let returnValue = allValue[getFieldId(fieldId)]

    if (
      !returnValue &&
      [FIELD_TYPES.CURRENCY, FIELD_TYPES.NUMBER].includes(field?.data_structure)
    ) {
      returnValue = 0
    }
    return returnValue
  }
}

const getParamValue = async (param, allValues, field, otherData) => {
  let computedValue = ''
  const key = param.substring(2)
  if (key === COMPUTED_RESERVED_KEYS.PROCESS_PREFIX) {
    computedValue = otherData?.processPrefix
  } else if (key === COMPUTED_RESERVED_KEYS.PROCESS_REFERENCE_NUM) {
    computedValue = otherData?.processReferenceNum
  } else if (key === COMPUTED_RESERVED_KEYS.CURRENT_FINANCIAL_YEAR) {
    computedValue = getCurrentFinancialYear()
  } else if (key === COMPUTED_RESERVED_KEYS.VERSION) {
    computedValue = otherData?.version
  } else if (key === COMPUTED_RESERVED_KEYS.COUNTER) {
    if (otherData.user) {
      const counterRes = await counterService.getCount(
        otherData.user,
        allValues[getFieldId(field.metadata.counterFieldTemplateId)],
        allValues[getFieldId(field.metadata.pivotFieldTemplateId)],
        field.id,
        otherData.processTemplateId,
        otherData.processReferenceNum,
      )
      computedValue = counterRes?.data?.counter
    }
  } else if (key === COMPUTED_RESERVED_KEYS.SL_NO) {
    computedValue = otherData?.slNo
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_EMPLOYEE_ID) {
    computedValue = otherData?.user?.employee_id || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_FIRST_NAME) {
    computedValue = otherData?.user?.first_name || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_LAST_NAME) {
    computedValue = otherData?.user?.last_name || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_ID) {
    computedValue = otherData?.user?.id || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_EMAIL) {
    computedValue = otherData?.user?.email || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_DEPARTMENT) {
    computedValue = otherData?.user?.department || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_DESIGNATION) {
    computedValue = otherData?.user?.designation || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_REPORTING_TO) {
    computedValue = otherData?.user?.reporting_to_user_id || ''
  } else if (key === COMPUTED_RESERVED_KEYS.LOGGED_IN_USER_SUBFUNCTION) {
    computedValue = otherData?.user?.sub_department || ''
  }
  return computedValue
}

// Add this new function
const computeDateField = async (fieldMetadata, allValues, listDataMetadata, otherData) => {
  const dateConfig = fieldMetadata?.date_config
  if (!dateConfig) return ''

  const { source_date, duration, date_computation_category } = dateConfig
  let baseDateStr = null

  if (
    source_date?.date_computation_source_type === DATE_COMPUTATION_FIELDS.FIELD &&
    source_date?.field_template_id
  ) {
    baseDateStr = allValues[`f_${source_date.field_template_id}`] || null
  } else if (
    source_date?.date_computation_source_type === DATE_COMPUTATION_FIELDS.ACTIVITY &&
    source_date?.activity_template_id
  ) {
    const res =
      otherData?.allActivitiesCreatedAtWithVersion ??
      (await processDataService.getActivityVersionInfo(
        otherData.user,
        otherData.processTemplateId,
        otherData.processReferenceNum,
      ))

    const activityRecord = res?.activityData?.find(
      (it) => it.activity_template_id == source_date.activity_template_id,
    )

    if (!activityRecord) {
      throw new Error(
        `Activity (ID: ${source_date.activity_template_id}) has not been completed yet. Please complete it before proceeding.`,
      )
    }

    if (source_date.version_type === ACTIVITY_VERSION.INITIAL_VERSION) {
      baseDateStr = activityRecord.initial_version_created_at || null
    } else {
      baseDateStr = activityRecord.latest_version_created_at || null
    }
  }

  if (!baseDateStr) return ''

  let durationDays = 0
  if (
    duration?.duration_category_type === DATE_COMPUTATION_FIELDS.LIST &&
    duration?.list_field_template_id
  ) {
    const selectedListItemId = allValues[`f_${duration.list_field_template_id}`]

    if (selectedListItemId) {
      const listItem = listDataMetadata?.find((it) => it.list_data_id == selectedListItemId)
      const metadata = listItem?.list_data_metadata

      let rawDays = null

      // Use the user-selected metadata key directly
      if (duration?.list_metadata_key && metadata && typeof metadata === 'object') {
        rawDays = metadata[duration.list_metadata_key] ?? null
      }

      // Fallback to list_data_value if metadata key gave nothing
      if (rawDays === null || rawDays === undefined) {
        rawDays = listItem?.list_data_value
      }

      const numericRaw = String(rawDays).match(/\d+(\.\d+)?/)?.[0]
      durationDays = Number(numericRaw) || 0
    }
  } else if (
    duration?.duration_category_type === DATE_COMPUTATION_FIELDS.FIELD &&
    duration?.numeric_field_template_id
  ) {
    const selectedFieldItemId = allValues[`f_${duration.numeric_field_template_id}`]
    durationDays = Number(selectedFieldItemId) || 0
  }

  // Date Computation based on calender-days/ working-days
  const inputFormat = baseDateStr.match(/^\d{2}-\d{2}-\d{4}$/) ? 'DD-MM-YYYY' : undefined

  const startMoment = moment(baseDateStr, inputFormat)

  if (!startMoment.isValid()) return ''

  // CALENDAR DAYS
  if (
    !date_computation_category ||
    date_computation_category === DATE_COMPUTATION_DAYS_CATEGORY.CALENDER_DAYS
  ) {
    const result = startMoment.clone().add(durationDays, 'days')
    return result.isValid() ? result.format(FORMATS.DATE) : ''
  }

  // WORKING DAYS

  if (date_computation_category === DATE_COMPUTATION_DAYS_CATEGORY.WORKING_DAYS) {
    const officeStartTime = 9.5 // 9:30 AM
    const officeEndTime = 18 // 6:00 PM
    const minutesInHour = 60

    const holidayList = otherData?.holidayList || []

    const isHoliday = (date) => {
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()

      const holidays = holidayList.filter((holiday) => holiday.y === year && holiday.m === month)

      return holidays.some((holiday) => holiday.d.some((d) => d.day === day))
    }

    const isWeekend = (date) => {
      const day = date.getDay()
      return day === 0 || day === 6
    }

    const getOfficeMinutesInDay = (date) => {
      if (isWeekend(date) || isHoliday(date)) return 0
      return (officeEndTime - officeStartTime) * minutesInHour
    }

    const totalRequiredMinutes = durationDays * (officeEndTime - officeStartTime) * minutesInHour

    let accumulatedMinutes = 0
    let currentDate = startMoment.clone().toDate()

    while (accumulatedMinutes < totalRequiredMinutes) {
      const minutesToday = getOfficeMinutesInDay(currentDate)

      if (minutesToday > 0) {
        accumulatedMinutes += minutesToday
      }

      currentDate.setDate(currentDate.getDate() + 1)
    }
    return moment(currentDate).format(FORMATS.DATE)
  }

  // fallback safety
  return startMoment.format(FORMATS.DATE)
}

const getDefaultComputeFieldValue = (operation) => {
  switch (operation) {
    case COMPUTED_OPERATION_TYPES.SUM:
    case COMPUTED_OPERATION_TYPES.SUM_TABLE_COLUMN:
      return 0
    case COMPUTED_OPERATION_TYPES.MULTIPLY:
    case COMPUTED_OPERATION_TYPES.MULTIPLY_TABLE_COLUMN:
      return 1
    case COMPUTED_OPERATION_TYPES.CONCAT:
    case COMPUTED_OPERATION_TYPES.CONCAT_TABLE_COLUMN:
      return ''
    default:
      return ''
  }
}

export const getParamId = (param, field) => {
  const isPolarityAppended = [
    COMPUTED_OPERATION_TYPES.SUM,
    COMPUTED_OPERATION_TYPES.MULTIPLY,
  ].includes(field?.metadata?.operation)

  return isPolarityAppended
    ? +param.substring(3, param.indexOf('.'))
    : +param.substring(2, param.indexOf('.'))
}
